import { useState, useCallback, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { getApiBaseUrl } from '@/services/api';
import { getAuthToken } from '@/services/firebase';

const SIGNED_URL_PATTERN = /(\.(s3(\.[^.]+)?\.amazonaws\.com|storage\.googleapis\.com)\/).*[?&](X-Amz-|X-Goog-|Expires=|Signature=)/;

function isSignedStorageUrl(url: string): boolean {
  return SIGNED_URL_PATTERN.test(url);
}

async function fetchFreshSignedUrl(expiredUrl: string): Promise<string | null> {
  try {
    const token = await getAuthToken();
    const res = await fetch(`${getApiBaseUrl()}/storage/refresh-signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ url: expiredUrl }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.url || null;
  } catch {
    return null;
  }
}

interface RefreshableImageProps {
  src: string;
  alt?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
}

export function RefreshableImage({ src, alt, className, loading = 'lazy', onLoad }: RefreshableImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [state, setState] = useState<'loading' | 'loaded' | 'refreshing' | 'expired'>('loading');
  const attemptedRef = useRef(false);

  const handleError = useCallback(async () => {
    if (attemptedRef.current || !isSignedStorageUrl(src)) {
      setState('expired');
      return;
    }
    attemptedRef.current = true;
    setState('refreshing');

    const freshUrl = await fetchFreshSignedUrl(src);
    if (freshUrl) {
      setCurrentSrc(freshUrl);
      setState('loading');
    } else {
      setState('expired');
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setState('loaded');
    onLoad?.();
  }, [onLoad]);

  if (state === 'expired') {
    return (
      <div className={`flex items-center justify-center bg-muted/30 rounded-lg ${className || ''}`} style={{ aspectRatio: '16/9' }}>
        <div className="flex flex-col items-center gap-1 text-muted-foreground/40">
          <ImageOff className="w-5 h-5" />
          <span className="text-[10px]">Preview expired</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {state === 'refreshing' && (
        <div className={`flex items-center justify-center bg-muted/20 rounded-lg animate-pulse ${className || ''}`} style={{ aspectRatio: '16/9' }}>
          <span className="text-[10px] text-muted-foreground/40">Loading preview...</span>
        </div>
      )}
      <img
        src={currentSrc}
        alt={alt || ''}
        className={`${className || ''} ${state === 'refreshing' ? 'hidden' : ''}`}
        loading={loading}
        onError={handleError}
        onLoad={handleLoad}
      />
    </>
  );
}

/**
 * Attach to a container element to auto-refresh expired signed URLs on <img> error events.
 * Used for dangerouslySetInnerHTML containers (like MarkdownPreview) where we can't use React components.
 */
export function useImageRefreshDelegation(_containerRef: React.RefObject<HTMLElement | null>) {
  const processedRef = useRef(new WeakSet<HTMLImageElement>());

  const handleError = useCallback(async (e: Event) => {
    const img = e.target as HTMLImageElement;
    if (img.tagName !== 'IMG') return;
    if (processedRef.current.has(img)) return;
    processedRef.current.add(img);

    const originalSrc = img.getAttribute('src') || '';
    if (!isSignedStorageUrl(originalSrc)) {
      img.style.display = 'none';
      return;
    }

    // Show placeholder while refreshing
    img.style.opacity = '0.3';

    const freshUrl = await fetchFreshSignedUrl(originalSrc);
    if (freshUrl) {
      img.src = freshUrl;
      img.style.opacity = '';
    } else {
      // Replace with expired placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'flex items-center justify-center bg-muted/30 rounded-lg';
      placeholder.style.aspectRatio = '16/9';
      placeholder.style.maxWidth = '100%';
      placeholder.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;gap:4px;color:var(--muted-foreground, #888);opacity:0.4">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" y1="2" x2="22" y2="22"/><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"/><line x1="13.5" y1="13.5" x2="6" y2="21"/><line x1="18" y1="12" x2="21" y2="15"/><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.052-.22 1.41-.59"/><path d="M21 15V5a2 2 0 0 0-2-2H9"/></svg>
          <span style="font-size:10px">Preview expired</span>
        </div>
      `;
      img.replaceWith(placeholder);
    }
  }, []);

  // Return event handlers to attach - caller controls when to use
  return { handleImageError: handleError };
}
